"use client";
import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { wrapper, button, iconContainer, content, inner } from './styles.js';
import { useTheme } from '../utils/theming/useTheme.js';
import { useFocused } from '../utils/hooks/useFocused.web.js';
import { ThemeProvider } from 'styled-components';
import { IconChevronUp } from '../icons/ui_navigation/IconChevronUp.web.js';
import { IconChevronDown } from '../icons/ui_navigation/IconChevronDown.web.js';
import { Box } from '../Box/Box.web.js';
import { useSmartControlled } from '../utils/hooks/useSmartControlled.js';
import { px2rem } from '../utils/helpers/style.js';
import { renderComponentOrElement } from '../utils/helpers/component.js';
import { lineHeights } from '../constants/line-heights.js';

var _excluded = ["opened", "onChange", "contentMaxHeight", "status", "borderStyle", "children", "closeText", "openText", "leftIcon", "color", "id", "overflow", "variant", "size", "textColor", "headerAriaLabel"];
var getAccordionLabel = function getAccordionLabel(openedState, openText, closeText) {
  if (openedState && typeof closeText === 'string') {
    return closeText;
  } else if (typeof openText === 'string') {
    return openText;
  }
  return undefined;
};
var Accordion = function Accordion(_ref) {
  var opened = _ref.opened,
    onChange = _ref.onChange,
    _ref$contentMaxHeight = _ref.contentMaxHeight,
    contentMaxHeight = _ref$contentMaxHeight === undefined ? 600 : _ref$contentMaxHeight,
    _ref$status = _ref.status,
    status = _ref$status === undefined ? 'default' : _ref$status,
    borderStyleProp = _ref.borderStyle,
    children = _ref.children,
    closeText = _ref.closeText,
    openText = _ref.openText,
    leftIcon = _ref.leftIcon,
    colorProp = _ref.color,
    _ref$id = _ref.id,
    id = _ref$id === undefined ? 'elemental-Accordion' : _ref$id,
    overflowProp = _ref.overflow,
    variantProp = _ref.variant,
    sizeProp = _ref.size,
    textColor = _ref.textColor,
    headerAriaLabel = _ref.headerAriaLabel,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useSmartControlled = useSmartControlled(false, opened, onChange),
    _useSmartControlled2 = _slicedToArray(_useSmartControlled, 2),
    openedState = _useSmartControlled2[0],
    setOpened = _useSmartControlled2[1];
  var _useFocused = useFocused(),
    focused = _useFocused.focused,
    onFocus = _useFocused.onFocus,
    onBlur = _useFocused.onBlur;
  var _React$useState = React.useState('hidden'),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    overflow = _React$useState2[0],
    setOverflow = _React$useState2[1];
  var toggleAccordion = React.useCallback(function () {
    setOpened(!openedState);
  }, [openedState, setOpened]);
  var _React$useState3 = React.useState(!openedState),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    hidden = _React$useState4[0],
    setHidden = _React$useState4[1];
  var theme = useTheme();
  var variant = variantProp || theme.variant;
  var size = variant === 'condensed' ? 's' : sizeProp !== null && sizeProp !== undefined ? sizeProp : theme.preferredComponentSize;
  var borderStyle = status === 'plain' ? 'none' : borderStyleProp || (variant === 'condensed' ? 'bottomOnly' : 'full');
  React.useEffect(function () {
    if (openedState) {
      setHidden(false);
    }
  }, [openedState, setHidden]);
  var color = colorProp || (status === 'plain' ? theme.accordion.web.plainColor : theme.colors.primary);
  var labelVerticalPadding = theme.accordion.web.sizings[size].button.paddingVertical;
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(wrapper, _extends({
    $variant: variant,
    $borderStyle: borderStyle,
    "data-testid": "elemental-Accordion",
    $status: status,
    $opened: openedState,
    $focused: focused,
    $textColor: textColor
    // this is for an actual use case where you want to overflow accordion
    // example in Readme
    ,
    overflow: overflow === 'visible' ? overflow : undefined,
    $size: size
  }, props), /*#__PURE__*/React.createElement(button, {
    $variant: variant,
    $status: status,
    $borderStyle: borderStyle,
    onFocusCapture: onFocus,
    onBlurCapture: onBlur,
    $color: color,
    $textColor: textColor,
    $focused: focused,
    $size: size,
    onClick: function onClick() {
      toggleAccordion();
      // set hidden for each transition, transitionEnd below restores the state
      setOverflow('hidden');
    },
    $opened: openedState,
    "aria-expanded": openedState,
    type: "button",
    "aria-label": headerAriaLabel || getAccordionLabel(openedState, openText, closeText),
    "data-testid": "elemental-Accordion-button",
    "aria-live": "polite",
    id: "".concat(id, "-button"),
    "aria-controls": "".concat(id, "-content"),
    style: {
      paddingLeft: leftIcon ? px2rem(52) : undefined
    }
  }, leftIcon && /*#__PURE__*/React.createElement(iconContainer, {
    style: {
      left: px2rem(14)
    },
    "aria-hidden": true
  }, renderComponentOrElement(leftIcon, {
    size: 24
  })), closeText && /*#__PURE__*/React.createElement(Box, {
    tag: "span",
    "aria-hidden": !openedState,
    pt: labelVerticalPadding,
    mb: labelVerticalPadding,
    lineHeight: lineHeights.normal,
    display: !openedState ? 'none' : 'inline-block'
  }, closeText), /*#__PURE__*/React.createElement(Box, {
    tag: "span",
    "aria-hidden": Boolean(closeText) && openedState,
    pt: labelVerticalPadding,
    mb: labelVerticalPadding,
    lineHeight: lineHeights.normal,
    display: closeText && openedState ? 'none' : 'inline-block'
  }, openText), /*#__PURE__*/React.createElement(iconContainer, {
    "aria-hidden": true
  }, openedState ? /*#__PURE__*/React.createElement(IconChevronUp, {
    size: 24
  }) : /*#__PURE__*/React.createElement(IconChevronDown, {
    size: 24
  }))), /*#__PURE__*/React.createElement(content, {
    $maxHeight: !openedState ? '0rem' : px2rem(contentMaxHeight),
    $overflow: overflow,
    "aria-hidden": !openedState,
    role: "region",
    id: "".concat(id, "-content"),
    "aria-labelledby": "".concat(id, "-button"),
    onTransitionEnd: function onTransitionEnd() {
      // after open/close set proper overflow
      if (openedState) {
        setOverflow(overflowProp);
      } else {
        setOverflow('hidden');
        setHidden(true);
      }
    }
  }, /*#__PURE__*/React.createElement(inner, {
    $borderStyle: borderStyle,
    $status: status,
    hidden: hidden,
    $size: size,
    "data-testid": "elemental-Accordion-content"
  }, children))));
};

export { Accordion };
